import { Component, Host, h, State, Element, Listen, Prop } from '@stencil/core';
import Splide, { Options } from '@splidejs/splide';
import { getCurrentMediaQuery } from '../../../utils/getCurrentMediaQuery';

@Component({
    tag: 'sew-ws-stage',
    styleUrl: 'sew-ws-stage.scss',
    shadow: false,
})
export class SewWsStage {
    private bigSwiperOptions: Options = {
        classes: {
            pagination: 'splide-pagination',
            page: 'splide-pagination-item',
        },
        mediaQuery: 'min',
        gap: 0,
        direction: 'ltr',
        perPage: 1,
        pagination: true,
        arrows: false,
        speed: 2000,
        type: 'fade',
        lazyLoad: 'nearby',
    };

    private smallSwiperOptions: Options = {
        type: 'loop',
        pauseOnHover: false,
        speed: 2000,
        pagination: false,
        mediaQuery: 'min',
        gap: 0,
        direction: 'ltr',
        perPage: 1,
        perMove: 1,
        start: 1,
        arrows: false,
        autoplay: true,
        interval: 5000,
        breakpoints: {
            560: {
                perPage: 2,
            },
            1024: {
                perPage: 3,
            },
            1212: {
                perPage: 4,
            },
        },
    };

    @Element() el: HTMLSewWsStageElement;
    @Prop() showPager = false;
    @Prop({ reflect: true }) compact = false;

    private swiperSmall: Splide;
    private swiperBig: Splide;
    private smallSliderRef?: HTMLDivElement;
    private bigSliderRef?: HTMLDivElement;
    private sectionBridgeRef: HTMLDivElement;
    private sliderElementCount = 0;
    private suppressSlideChangeEvent = false;
    private videoElement;
    private videoIsRunning = false;
    private hideSlider = false;
    @State() private clonedItems = [];
    @State() private hasSmallSlider: boolean;
    @State() private hasMobileHeadline = false;

    @Listen('scroll', { target: 'window' })
    onScroll(): void {
        if (this.smallSliderRef && !this.showPager) {
            const windowCenter = (window.innerHeight * 3) / 4;

            // add half sliderheight if it is hidden, because the disappearing bridge influences the slider position
            const sliderPosition =
                this.smallSliderRef.getBoundingClientRect().top +
                (this.hideSlider ? 0 : this.smallSliderRef.offsetHeight / 2);

            const hideSlider = sliderPosition < windowCenter && window.scrollY > 10;
            this.hideSlider = hideSlider;

            this.smallSliderRef.classList.toggle('swiper-minimize', hideSlider);
            this.bigSliderRef.classList.toggle('swiper-minimize', hideSlider);
            this.sectionBridgeRef.classList.toggle('bridge-minimize', hideSlider);
        }
    }

    @Listen('resize', { target: 'window' })
    onResize(): void {
        this.hasMobileHeadline = this.compact && getCurrentMediaQuery() === 1;
    }

    private setShowImageOnPictures(): void {
        const pictures = this.el.querySelectorAll('sew-picture');

        pictures.forEach(picture => (picture.showImage = true));
    }

    private removeAutoplayPoster(): void {
        // remove all posters from videos with autoplay
        const items = this.el.querySelectorAll('sew-ws-stage-item');

        items.forEach(item => {
            if (item.autoplay !== true) return;

            const poster = item.querySelector('sew-video sew-picture');

            if (poster) {
                poster.parentNode.removeChild(poster);
            }
        });
    }

    componentWillLoad(): void {
        this.removeAutoplayPoster();
        this.onResize();
    }

    componentDidLoad(): void {
        this.setShowImageOnPictures();
        this.cloneSwiperItems();
    }

    componentDidUpdate(): void {
        if (this.hasSmallSlider) {
            this.swiperSmall = new Splide('.swiper-small', this.smallSwiperOptions);
            this.swiperSmall.mount();
            this.swiperSmall.on('move', this.updateStageSmall);
            this.swiperSmall.on('click', this.onSwiperSlideClick);
        }
        this.swiperBig = new Splide('.swiper-big', this.bigSwiperOptions);
        this.swiperBig.mount();
        this.swiperBig.on('active', this.onActive);
        this.swiperBig.on('move', this.onMove);
        this.onActive();
        document.body.style.setProperty(
            '--half-of-small-slider',
            this.smallSliderRef ? this.smallSliderRef.offsetHeight / 2 + 'px' : '0px'
        );
    }

    restartAvifImg(activeSplide) {
        const avifImg = activeSplide.querySelector('sew-picture') as HTMLSewPictureElement;

        if (!avifImg) return;

        const src = avifImg.src;

        if (src.endsWith('.avif')) {
            avifImg.reloadImage();
        }
    }

    private onActive = () => {
        const activeSplide = this.bigSliderRef.querySelector('.is-active') as HTMLSewWsStageItemElement;

        if (activeSplide.duration) {
            this.swiperSmall.Components.Autoplay.pause();
            setTimeout(() => {
                this.swiperSmall.go('+1');
                this.swiperSmall.Components.Autoplay.play();
            }, Number(activeSplide.duration));
        }

        this.restartAvifImg(activeSplide);

        if (!activeSplide.classList.contains('is-video')) {
            return;
        }

        this.videoElement = activeSplide.querySelector('sew-video');

        if (this.videoElement.classList.contains('no-consent')) {
            this.bigSliderRef && this.bigSliderRef.classList.add('no-consent');
        } else {
            this.bigSliderRef && this.bigSliderRef.classList.remove('no-consent');
        }

        // start youtube video
        if (activeSplide.autoplay === true) {
            if (this.videoElement.isReady) {
                this.startVideo();
            } else {
                this.videoElement.addEventListener(
                    'playerReady',
                    () => {
                        this.startVideo();
                    },
                    { once: true }
                );
            }
        } else {
            this.videoElement.addEventListener('videoPlaying', () => {
                this.startVideo();
            });

            this.videoElement.addEventListener('videoPaused', () => {
                this.pauseVideo();
            });
        }
    };

    private startVideo = () => {
        this.bigSliderRef.classList.add('video-plays');
        this.smallSliderRef && this.smallSliderRef.classList.add('video-plays');
        this.videoElement.startVideo();
        this.swiperSmall && this.swiperSmall.Components.Autoplay.pause();
        this.videoIsRunning = true;

        this.videoElement.addEventListener(
            'videoEnded',
            () => {
                this.stopVideo();
            },
            { once: true }
        );
    };

    private stopVideo = () => {
        if (!this.videoIsRunning) return;

        this.videoIsRunning = false;
        this.videoElement.stopVideo();
        this.bigSliderRef.classList.remove('video-plays');
        this.smallSliderRef && this.smallSliderRef.classList.remove('video-plays');
        this.swiperSmall && this.swiperSmall.go('+1');
        this.swiperSmall && this.swiperSmall.Components.Autoplay.play();
    };

    private pauseVideo = () => {
        this.videoIsRunning = false;
        this.bigSliderRef.classList.remove('video-plays');
        this.smallSliderRef && this.smallSliderRef.classList.remove('video-plays');
        this.swiperSmall && this.swiperSmall.Components.Autoplay.play();
    };

    private onMove = () => {
        if (this.videoIsRunning) {
            this.stopVideo();
        }
    };

    private updateStageSmall = () => {
        this.smallSliderRef.classList.add('is-moving');
        setTimeout(() => {
            this.smallSliderRef.classList.remove('is-moving');
        }, this.smallSwiperOptions.speed);
        if (this.suppressSlideChangeEvent) {
            setTimeout(() => {
                this.suppressSlideChangeEvent = false;
            });
            return;
        }

        const index =
            this.swiperSmall.index - 1 < 0 ? this.sliderElementCount - 1 : this.swiperSmall.index - 1;
        this.swiperBig.go(index);
    };

    private onSwiperSlideClick = event => {
        this.swiperSmall.go(event.index + 1);
    };

    private cloneSwiperItems() {
        const clonedItems = [];
        const childnodes = this.el.querySelectorAll('sew-ws-stage-item');
        this.hasSmallSlider = childnodes.length > 1;

        if (!this.hasSmallSlider) {
            childnodes[0].index = 0;
            return;
        }

        childnodes.forEach((node, index) => {
            (node as HTMLSewWsStageItemElement).index = index;
            clonedItems.push(
                <div class="splide__slide">
                    <div class="topic-line">{this.getTextContentOfNode(node, '.topic-line')}</div>
                    <div class="headline">{this.getTextContentOfNode(node, '.headline')}</div>
                </div>
            );

            this.sliderElementCount++;
        });

        this.clonedItems = clonedItems;

        this.adaptMaxItemsForSlider(clonedItems.length);
    }

    private adaptMaxItemsForSlider(amount: number) {
        if (amount >= 5) return;

        if (amount === 4) {
            this.smallSwiperOptions.breakpoints['1212'].perPage = 3;
        } else if (amount === 3) {
            this.smallSwiperOptions.breakpoints['1212'].perPage = 2;
            this.smallSwiperOptions.breakpoints['1024'].perPage = 2;
        } else if (amount === 2) {
            this.smallSwiperOptions.breakpoints['1212'].perPage = 1;
            this.smallSwiperOptions.breakpoints['1024'].perPage = 1;
            this.smallSwiperOptions.breakpoints['560'].perPage = 1;
        }
    }

    private getTextContentOfNode(node, classname) {
        return node.querySelector(classname).textContent;
    }

    private moveSliderLeft = () => {
        this.suppressSlideChangeEvent = true;
        this.swiperSmall.go('<');
    };

    private moveSliderRight = () => {
        this.suppressSlideChangeEvent = true;
        this.swiperSmall.go('>');
    };

    private getCompactMobileHeadline = () => {
        const firstItem = this.el.querySelector('sew-ws-stage-item');
        const headline = firstItem.headline;

        return headline;
    };

    render() {
        return (
            <Host class={{ 'no-progress-bar': !this.hasSmallSlider }}>
                <div class="stage">
                    <div
                        ref={el => (this.bigSliderRef = el as HTMLDivElement)}
                        class={{ 'splide swiper-big': true, 'show-pager': this.showPager }}
                    >
                        <div class="splide__track">
                            <div class="splide__list">
                                <slot></slot>
                            </div>
                        </div>
                    </div>
                    {this.hasSmallSlider && (
                        <div class="swiper-small-container">
                            <div class="grid">
                                <div
                                    class={{
                                        'swiper-scale-container': true,
                                        'swiper-minimize': this.showPager,
                                    }}
                                    ref={el => (this.smallSliderRef = el as HTMLDivElement)}
                                >
                                    <div class="swiper-button-prev" onClick={this.moveSliderLeft}>
                                        <sew-icon
                                            class="svg"
                                            icon="arrow-chevron-left"
                                            size="xl"
                                            width="48"
                                            height="48"
                                        ></sew-icon>
                                    </div>
                                    <div class="splide swiper-small">
                                        <div class="splide__track">
                                            <div class="splide__list">{this.clonedItems}</div>
                                        </div>
                                    </div>
                                    <div class="swiper-button-next" onClick={this.moveSliderRight}>
                                        <sew-icon
                                            class="svg"
                                            icon="arrow-chevron-right"
                                            size="xl"
                                            width="48"
                                            height="48"
                                        ></sew-icon>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {this.hasSmallSlider && !this.showPager && (
                        <div
                            class="section-bridge"
                            ref={el => (this.sectionBridgeRef = el as HTMLInputElement)}
                        ></div>
                    )}
                </div>
                {this.hasMobileHeadline && (
                    <div class="mobile-headline">
                        <h1 class="headline">{this.getCompactMobileHeadline()}</h1>
                    </div>
                )}
            </Host>
        );
    }
}
